// Product card image carousel — auto-loops through gallery images on
// any `.product-quick.has-carousel` card. Dots stay in sync; clicking
// a dot jumps to that slide and resets the auto-rotation timer.
//
// Single-image cards render NO carousel markup at all (no
// `data-product-carousel` attribute, no dots), so this module simply
// finds and ignores them — no defensive branching needed.
//
// Pauses while the user hovers or focuses inside the card so they can
// linger on a specific image without it sliding away.

const ROTATE_MS = 3500;          // gap between slide changes
const TRANSITION_MS = 550;       // matches the CSS opacity transition

/**
 * Build a controller around one card. Encapsulates the state (active
 * index, timer handle) and returns destroy/play/pause hooks.
 */
function controllerFor(card) {
    const slides = Array.from(card.querySelectorAll('.product-quick__slide'));
    const dots   = Array.from(card.querySelectorAll('[data-carousel-dot]'));

    // Belt-and-braces: if the markup somehow lost its slides, bail.
    if (slides.length < 2) return null;

    let index = slides.findIndex((s) => s.classList.contains('is-active'));
    if (index < 0) index = 0;

    let timer = null;
    let paused = false;

    const show = (next) => {
        if (next === index) return;
        slides[index]?.classList.remove('is-active');
        dots[index]?.classList.remove('is-active');
        index = (next + slides.length) % slides.length;   // wrap both ways
        slides[index]?.classList.add('is-active');
        dots[index]?.classList.add('is-active');
    };

    const tick = () => {
        if (!paused) show(index + 1);
    };

    const play  = () => { if (!timer) timer = window.setInterval(tick, ROTATE_MS); };
    const stop  = () => { if (timer)  { window.clearInterval(timer); timer = null; } };

    // Hover pauses (but doesn't stop the timer — just suppresses the
    // tick) so when the user leaves the card the rhythm continues
    // without a sudden jump.
    card.addEventListener('mouseenter', () => { paused = true; });
    card.addEventListener('mouseleave', () => { paused = false; });
    card.addEventListener('focusin',  () => { paused = true; });
    card.addEventListener('focusout', () => { paused = false; });

    // Dot clicks → jump to that slide AND reset the timer so the next
    // auto-advance happens a full interval later (otherwise it'd feel
    // jittery if the click lands right before a scheduled tick).
    dots.forEach((dot, i) => {
        dot.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();   // don't trigger the parent <a> navigation
            show(i);
            stop();
            play();
        });
    });

    play();
    return { stop };
}

function init() {
    const cards = document.querySelectorAll('.product-quick.has-carousel[data-product-carousel]');
    cards.forEach((card) => controllerFor(card));
}

// Hydrate on DOM ready. Re-running is safe — the function is
// idempotent for cards already initialized (each adds its own listeners
// but the timer key lives in closure, not on the DOM, so re-init would
// double-up. Guard against that with a `data-init` flag).
function safeInit() {
    document.querySelectorAll('.product-quick.has-carousel:not([data-carousel-init])').forEach((card) => {
        card.setAttribute('data-carousel-init', '1');
        controllerFor(card);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
} else {
    safeInit();
}

// Re-run after WC fragment refreshes (cart updates can rerender cards).
document.body?.addEventListener?.('wc_fragments_refreshed', safeInit);
